import { IDateRange } from "@app/interfaces/IDateRange";
import { DateRange } from "@enums/DateRange.enum";
import { SortDirection } from "@enums/SortDirection";
import { TranslationService } from "@services/translation.service";
import * as moment from "moment";
import "moment-duration-format";

export type FormatDateTypes = "full" | "noyear" | "date" | "time" | "timeago" | "timeortimeago" | "timeordate" | "fulltime" | "fulldatetime" | "isostring" | string;


export class VeriDate {

    readonly timeFormat = "HH:mm"; // 13:23
    readonly fullTimeFormat = "HH:mm:ss"; // 13:23:00
    readonly dayFormat = "Do";
    readonly monthFormat = "MMM";
    readonly yearFormat = "YYYY";

    readonly dayMonthFormat = `${this.dayFormat} ${this.monthFormat}`; // 3rd May 2016
    readonly dateFormat = `${this.dayMonthFormat} ${this.yearFormat}`; // 3rd May 2016

    readonly dateTimeFormat = `${this.timeFormat}, ${this.dateFormat}`; // 13:23, 3rd May 2016
    readonly fullDateTimeFormat = `${this.fullTimeFormat}, ${this.dateFormat}`; // 13:23:21, 3rd May 2016

    static $inject = ["translationService"]

    constructor(private readonly translationService: TranslationService) {
    }

    formatDate(date: string | Date | moment.Moment, formatType?: FormatDateTypes): string {
        if (formatType) {
            formatType = formatType.toLowerCase();
        }

        // If date is not sent through, the moment constructor would create a datetime of now, we should actually respond with ""
        if (!date) {
            if (formatType === "timeago") {
                return this.translationService.t("Common.Never");
            }

            return "";
        }

        let outputFormat = "";
        let momentDate: moment.Moment;

        if (date instanceof Date || typeof date === "string") {
            momentDate = this.createMoment(date);
        }
        else {
            momentDate = date;
        }

        if (!moment.isMoment(momentDate)) {
            // Unable to format
            return momentDate;
        }

        // Default option or "full"
        if (!formatType || formatType === "full") {
            outputFormat = this.dateTimeFormat;
        }
        else if (formatType === "fulldatetime") {
            outputFormat = this.fullDateTimeFormat;
        }
        else if (formatType === "noyear") {
            outputFormat = `${this.timeFormat}, ${this.dayFormat} ${this.monthFormat}`;
        }
        else if (formatType === "date") {
            outputFormat = this.dateFormat;
        }
        else if (formatType === "time") {
            // If we are time only, show am/pm to give context
            outputFormat = `${this.timeFormat}`; // dropped the a
        }
        else if (formatType === "timeordate") {
            // If we are time only, show am/pm to give context
            if (momentDate.isSame(this.createMoment(new Date()), "d")) {
                outputFormat = `${this.timeFormat}a`;
            }
            else {
                outputFormat = this.dateFormat;
            }
        }
        else if (formatType === "fulltime") {
            // If we are time only, show am/pm to give context
            outputFormat = `${this.fullTimeFormat}`; // dropped the a
        }
        else if (formatType === "timeago" || formatType === "duration") {
            if (Math.abs(this.createMoment(new Date()).diff(momentDate, "s")) < 60) { // 1000 milliseconds
                return this.translationService.t("VeriDateService.JustNow");
            }
            if (Math.abs(this.createMoment(new Date()).diff(momentDate, "years")) > 5) { // older than 5 years
                return this.translationService.t("VeriDateService.LongTimeAgo");
            }

            // Todo: From Now is expensive
            //debugger;
            console.warn("timeago > slow performance");
            return momentDate.fromNow(formatType === "duration");
        }
        else if (formatType === "timeortimeago") {
            if (momentDate.clone().startOf("day").diff(this.createMoment(new Date()).startOf("day")) >= 0) {
                outputFormat = this.timeFormat;
            }
            else {
                console.warn("timeago > slow performance");
                return momentDate.fromNow();
            }
        }
        else if (formatType === "dayofweek") {
            return this.getDayOfWeek(momentDate.day())
        }
        else if (formatType === "isostring") {
            return momentDate.toISOString();
        }
        else {
            outputFormat = this.dateTimeFormat;
        }

        return momentDate.format(outputFormat);
    }

    formatDurationMs(durationMs: number) {
        const d = moment.duration(durationMs);

        if (d.asDays() >= 365) {
            return (d as any).format("y [years], M [months]");
        }

        if (d.asDays() >= 1) {
            return (d as any).format("d [days], h [hours]");
        }

        return (d as any).format("h [hours], m [mins]");
    }

    getDiff(startTime: moment.Moment | string | Date, endTime: moment.Moment | string | Date, unitOfTime?: moment.unitOfTime.Diff) {
        if (!moment.isMoment(startTime)) {
            startTime = this.createMoment(startTime);
        }

        if (!moment.isMoment(endTime)) {
            endTime = this.createMoment(endTime);
        }

        return endTime.diff(startTime, unitOfTime);
    }

    formatDuration(startTime: moment.Moment | string | Date, endTime: moment.Moment | string | Date) {
        const ms = this.getDiff(startTime, endTime);

        return this.formatDurationMs(ms);
    }

    formatDatesFromAndTo(start: Date | string, end?: Date | string): string {
        let formattedDate = "";
        const startMoment = this.createMoment(start);
        const endMoment = end ? this.createMoment(end) : null;

        start = new Date(start);

        if (!end) {
            return this.translationService.t('VeriDateService.FromToNow', startMoment.format(this.dateTimeFormat));
        }

        if (startMoment.isSame(endMoment, 'day')) {
            formattedDate = this.translationService.t('VeriDateService.FromTime1ToTime2OnDate', [startMoment.format(this.timeFormat), endMoment.format(this.timeFormat), startMoment.format(this.dateFormat)]);
        }
        else {
            formattedDate = this.translationService.t('VeriDateService.FromDate1ToDate2', [startMoment.format(this.dateTimeFormat), endMoment.format(this.dateTimeFormat)]);
        }

        return formattedDate;
    }

    roundDate(date: moment.Moment, roundBy = 15, roundUp: boolean) {

        if (roundUp) {
            const remainder = roundBy - (date.minute() % roundBy);
            date.add(remainder, "m");
        }

        else {
            const remainder = (date.minute() % roundBy);
            date.subtract(remainder, "m");
        }
    }

    formatTimeDuration(seconds: number): string {
        return seconds
            ? moment().startOf("day").seconds(seconds).format("mm:ss")
            : "";
    }

    sort(date1: string | Date, date2: string | Date, sortDirection: SortDirection = SortDirection.Descending): number {
        var parsedDate1 = new Date(date1);
        var parsedDate2 = new Date(date2);

        const parsedDate1Time = parsedDate1.getTime();
        if (isNaN(parsedDate1Time)) {
            console.error(date1, "is not a date", typeof date1);
        }

        if (sortDirection == SortDirection.Ascending) {
            return parsedDate1Time - parsedDate2.getTime();
        }
        else {
            return parsedDate2.getTime() - parsedDate1Time;
        }
    }

    createDate(date: string | Date) {
        return new Date(date as Date);
    }

    createMoment(date: string | Date | moment.Moment): moment.Moment {
        if (date instanceof moment) {
            return date as moment.Moment;
        }

        return moment(this.createDate(date as Date));
    }

    date1IsBeforeDate2(date1: string | Date, date2: string | Date) {

        var parsedDate1 = date1 instanceof Date ? date1 : new Date(date1 as string);
        var parsedDate2 = date2 instanceof Date ? date2 : new Date(date2 as string);

        return parsedDate1.getTime() < parsedDate2.getTime();
    }

    date1IsAfterDate2(date1: string | Date, date2: string | Date) {

        var parsedDate1 = new Date(date1);
        var parsedDate2 = new Date(date2);

        return parsedDate1.getTime() > parsedDate2.getTime();
    }

    dateBetweenRange(date: string | Date, dateFrom: string | Date, dateTo: string | Date) {
        return this.date1IsAfterDate2(date, dateFrom) && this.date1IsAfterDate2(dateTo, date);
    }

    isSameDate(date1: Date, date2: Date): boolean {
        if (date1.getFullYear() === date2.getFullYear() &&
            date1.getMonth() === date2.getMonth() &&
            date1.getDate() === date2.getDate()) {
            return true;
        }

        var secondsDiff = (date2.getTime() - date1.getTime()) / 1000;

        // Sometimes we have exactly 24 hours difference it may not show up as the same day (i.e. the above condition)
        return date1.getHours() == 0 && date1.getMinutes() == 0 && secondsDiff == 86_400;
    }

    getDayOfWeek(day: number, shorten = false) {
        var translationKey = "";

        switch (day) {
            case 0:
                translationKey = "VeriDateService.Sunday";
                break;

            case 1:
                translationKey = "VeriDateService.Monday";
                break;

            case 2:
                translationKey = "VeriDateService.Tuesday";
                break;

            case 3:
                translationKey = "VeriDateService.Wednesday";
                break;

            case 4:
                translationKey = "VeriDateService.Thursday";
                break;

            case 5:
                translationKey = "VeriDateService.Friday";
                break;

            case 6:
                translationKey = "VeriDateService.Saturday";
                break;
        }

        if (shorten) {
            //translationKey += "Abbrv";
            return this.translationService.t(translationKey).substr(0, 3);
        }

        return this.translationService.t(translationKey);
    }

    //getDateMilliseconds(date: string): number {
    //    const d = new Date(date);
    //    return d.getMilliseconds();
    //}

    getReadableDateRangeLabel(start: Date | moment.Moment, end: Date | moment.Moment, dateRange: DateRange): string {
        const startDate = moment(start);
        const endDate = moment(end);

        if (dateRange != DateRange.Custom) {
            return this.translationService.tEnum(DateRange, "DateRange", dateRange);
        }

        if (startDate.isSame(endDate, "d")) {
            return this.formatDate(startDate, "date");
        }

        if (startDate.isSame(endDate, "month") && startDate.date() == 1 && endDate.date() == endDate.daysInMonth()) {

            return startDate.format("MMM YYYY");
        }

        var startDateString = this.formatDate(startDate, "date");
        if (startDate.isSame(endDate, "month") && startDate.isSame(endDate, "year")) {

            startDateString = startDate.format(this.dayFormat);
        }
        else if (startDate.isSame(endDate, "year")) {

            startDateString = startDate.format(this.dayMonthFormat);
        }

        return `${startDateString} to ${this.formatDate(endDate, "date")}`;
    }

    dateRangeIntoEnum(dateRange: IDateRange): DateRange {
        const startDate = moment(dateRange.start);
        const endDate = moment(dateRange.end);

        if (startDate.isSame(moment(), "days") && endDate.isSame(moment(), "days")) {
            return DateRange.Today;
        }
        else if (startDate.isSame(moment().add(-1, "days"), "day") && endDate.isSame(moment().add(-1, "days"), "day")) {
            return DateRange.Yesterday;
        }
        else if (startDate.isSame(moment().add(-7 - 1, "days"), "day") && endDate.isSame(moment().add(-1, "days"), "days")) {
            return DateRange.Last7Days;
        }
        else if (startDate.isSame(moment().add(-14 - 1, "days"), "day") && endDate.isSame(moment().add(-1, "days"), "days")) {
            return DateRange.Last14Days;
        }
        else if (startDate.isSame(moment().add(-30 - 1, "days"), "day") && endDate.isSame(moment().add(-1, "days"), "days")) {
            return DateRange.Last30Days;
        }
        else if (startDate.isSame(moment().add(-7, "days"), "day") && endDate.isSame(moment(), "days")) {
            return DateRange.Latest7Days;
        }
        else if (startDate.isSame(moment().add(-14, "days"), "day") && endDate.isSame(moment(), "days")) {
            return DateRange.Latest14Days;
        }
        else if (startDate.isSame(moment().startOf("isoWeek"), "day") && endDate.isSame(moment().endOf("isoWeek"), "day")) {
            return DateRange.ThisWeek;
        }
        else if (startDate.isSame(moment().add(-1, "weeks").startOf("isoWeek"), "day") && endDate.isSame(moment().add(-1, "weeks").endOf("isoWeek"), "day")) {
            return DateRange.LastWeek;
        }
        else if (startDate.isSame(moment().startOf("month"), "day") && endDate.isSame(moment().endOf("month"), "day")) {
            return DateRange.ThisMonth;
        }
        else if (startDate.isSame(moment().add(-1, "months").startOf("month"), "day") &&
            endDate.isSame(moment().add(-1, "months").endOf("month"), "day")) {
            return DateRange.LastMonth;
        }
        else {
            return DateRange.Custom;
        }
    }

    dateRangeFromEnum(dateRange: DateRange): IDateRange {
        let startMoment: moment.Moment = moment();
        let endMoment: moment.Moment = moment();

        var rangeEndsYesterday = false;

        switch (dateRange) {
            case DateRange.Today:
                startMoment = moment();
                break;

            case DateRange.Yesterday:
                startMoment = moment().subtract(1, "days");
                endMoment = startMoment.clone();
                break;

            case DateRange.Week:
                startMoment = moment().subtract(1, "weeks");
                rangeEndsYesterday = true;
                break;

            case DateRange.Last7Days:
                startMoment = moment().subtract(6, "days");
                rangeEndsYesterday = true;
                break;

            case DateRange.Last14Days:
                startMoment = moment().subtract(14, "days");
                rangeEndsYesterday = true;
                break;

            case DateRange.Last30Days:
                startMoment = moment().subtract(30, "days");
                rangeEndsYesterday = true;
                break;

            case DateRange.ThisWeek:
                startMoment = moment().startOf("isoWeek");
                endMoment = startMoment.clone().add(6, "d");
                break;

            case DateRange.ThisMonth:
                startMoment = moment().startOf("month");
                endMoment = moment().endOf("month");
                break;

            case DateRange.Latest3Weeks:
                startMoment = moment().startOf("isoWeek").subtract(2, "weeks").subtract(1, "day");
                break;

            case DateRange.Latest14Days:
                startMoment = moment().subtract(14, "days");
                break;

            case DateRange.Latest4Weeks:
                startMoment = moment().subtract(28, "days");
                break;

            case DateRange.Latest7Days:
                startMoment = moment().subtract(7, "days");
                break;

            case DateRange.LastWeek:
                startMoment = moment().subtract(1, "weeks").startOf("isoWeek");
                endMoment = moment().subtract(1, "weeks").endOf("isoWeek");
                break;

            case DateRange.LastMonth:
                startMoment = moment().subtract(1, "months").startOf("month");
                endMoment = moment().subtract(1, "months").endOf("month");
                break;

            case DateRange.Latest4Weeks:
                startMoment = moment().subtract(4, "weeks");
                break;
        }

        if (rangeEndsYesterday) {
            startMoment.subtract(1, "d");
            endMoment.subtract(1, "d");
        }

        return {
            start: startMoment.startOf("d").toDate(),
            end: endMoment.endOf("d").toDate()
        }
    }

    minutesToDaysHoursMinutes(inputMinutes: number) {
        //https://stackoverflow.com/a/37096512/94278
        //inputMinutes = Number(inputMinutes);

        var h = Math.floor(inputMinutes / 60);
        var m = Math.floor(inputMinutes % 60 / 60);
        var s = Math.floor(inputMinutes % 60 % 60);

        var hDisplay = h > 0 ? this.translationService.t('VeriDateService.Hours', h) : "";
        var mDisplay = m > 0 ? this.translationService.t('VeriDateService.Minutes', m) : "";
        var sDisplay = s > 0 ? this.translationService.t('VeriDateService.Seconds', s) : "";

        return hDisplay + mDisplay + sDisplay;
    }

    minutesToDurationString(inputMinutes: number, exceptionValue?: number, exceptionString?: string, shortHand = true): string {

        if (exceptionValue && exceptionValue === inputMinutes) {
            if (!exceptionString) {
                console.warn("minutesToDuration expects an 'exceptionString' when using 'exceptionValue'");
            }

            return exceptionString;
        }

        if (isNaN(inputMinutes)) {
            console.warn("minutesToDuration invalid input", inputMinutes);
        }

        // Ensure we round to nearest value
        inputMinutes = Math.round(inputMinutes);

        //console.log("minutesToDuration", inputMinutes);

        // Ensure we have a valid number
        inputMinutes = inputMinutes || 0;

        var displayMinutes: number;
        var displayHours: number;

        if (inputMinutes >= 0) {
            displayMinutes = Math.floor(inputMinutes % 60);
            displayHours = Math.floor(inputMinutes / 60);
        }
        else {
            displayMinutes = Math.ceil(inputMinutes % 60);
            displayHours = Math.ceil(inputMinutes / 60);
        }

        let hourDisplay = "h";
        let minDisplay = "m";

        if (!shortHand) {
            hourDisplay = " hour";
            minDisplay = " min";

            if (displayHours !== 1) {
                hourDisplay += "s";
            }

            if (displayMinutes !== 1) {
                minDisplay += "s";
            }
        }

        return displayHours !== 0
            ? `${displayHours}${hourDisplay}, ${displayMinutes}${minDisplay}`
            : `${displayMinutes}${minDisplay}`;
    }

    parseTimeSpan(timeSpan: string): number {
        //console.log("parseTimeSpan", timeSpan);
        // "33.15:35:12"
        var seconds = 0;
        var minutes = 0;
        var hours = 0;

        if (!timeSpan) {
            return 0;
        }

        if (typeof timeSpan === "number") {
            return parseInt(timeSpan);
        }

        if (timeSpan.indexOf(".") >= 0) {

            var dayHourSplit = timeSpan.split(".");
            seconds = parseFloat(`0.${dayHourSplit[1]}`) /*parseInt(dayHourSplit[0]) * 24;*/

            timeSpan = dayHourSplit[0];
        }

        var timeSplit = timeSpan.split(":");
        hours += parseInt(timeSplit[0]);
        minutes += parseInt(timeSplit[1]);
        seconds += parseInt(timeSplit[2]);

        return (hours * 60 * 60) + (minutes * 60) + seconds;
    }

    clamp(time: moment.Moment, start: moment.Moment, end: moment.Moment) {
        if (time.isBefore(start)) {
            return start;
        }
        else if (time.isAfter(end)) {
            return end;
        }

        else return time;
    }

    timeWithinRange(time: moment.Moment, start: moment.Moment, end: moment.Moment) {
        return time.isBetween(start, end);
    }

    setMomentToMinuteOfDay(moment: moment.Moment, totalMins: number): void {
        const hours = Math.floor(totalMins / 60);
        totalMins %= 60;
        const minutes = Math.round(totalMins);

        moment.set("hours", hours);
        moment.set("minutes", minutes);
    }

    convertTimeSpanToMins(timespan: string): number {
        const segments = timespan.split(":");

        const hourSeconds = +segments[0] * 60 * 60;
        const minuteSeconds = +segments[1] * 60;
        const seconds = +segments[2];

        let final = 0
        final += (hourSeconds) ? hourSeconds : 0;
        final += (minuteSeconds) ? minuteSeconds : 0;
        final += (seconds) ? seconds : 0;

        return Math.round(final / 60);
    }

    getTimeSpanDetailsFromMinuteOfDay(secondOfDay: number): { hour: number; minute: number; second: number } {
        const hours = Math.floor(secondOfDay / 3600);
        const minutes = Math.floor((secondOfDay % 3600) / 60);
        const seconds = Math.floor(secondOfDay % 60);

        return {
            hour: hours,
            minute: minutes,
            second: seconds
        }
    }

    convertMinsToTimeSpan(totalMins: number, includeSeconds: boolean = false): string {
        const hours = Math.floor(totalMins / 60);
        const hoursString = (hours < 10) ? `0${hours}` : `${hours}`;

        totalMins %= 60;
        const minutes = Math.round(totalMins);
        const minutesString = (minutes < 10) ? `:0${minutes}` : `:${minutes}`;

        const secondsString = (includeSeconds) ? `:00` : "";

        return `${hoursString}${minutesString}${secondsString}`;
    }

    convertMomentToTimespan(moment: moment.Moment): string {
        const minuteOfDate: number = this.getMinuteOfDayFromDate(moment.toDate());

        return this.convertMinsToTimeSpan(minuteOfDate);
    }

    getMinuteOfDayFromDate(date: Date): number {
        return (date.getHours() * 60) + date.getMinutes();
    }

    getDateInInputFormat(date: Date): string {
        //Return a date in YYYY-MM-DD format string to use as html date input placeholder
        date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
        return date.toJSON().slice(0, 10);
    }

    getMinuteOfDayFromMoment(value: moment.Moment): number {
        if (!moment.isMoment(value)) {
            return 0;
        }

        return (value.get('hour') * 60) + value.get('minute');
    }
}
